const Function = require('core/src/function');
const IGraphileon = require('core/src/i-graphileon').default;
const Language = require('core/src/language').default;
const Log = require('core/src/log');
const _ = require('core/src/utils/legacy');

const log = Log.instance("Function/Dialog");

const Dialog = Function.extend(Function, "Dialog");

const DIALOG_TYPES = ['alert', 'confirm', 'prompt'];
const ALERT_TYPES  = ['default', 'primary', 'success', 'info', 'warning', 'danger'];
const MODAL_SIZES  = ['modal-sm', 'modal-md', 'modal-lg', 'modal-xl'];
const INPUT_TYPES = [
	'text', 'email', 'number', 'password', 'tel', 'url', 'search',
	'date', 'datetime-local', 'time', 'month', 'week', 'color'
];

Dialog.prototype._validateButtons = function(buttons) {
	if (buttons == null) return true;
	if (!_.isArray(buttons) && !_.isPlainObject(buttons)) {
		return this.language.translate('buttons must be an array or object');
	}
	const list = _.isArray(buttons)
		? buttons
		: _.values(buttons);
	for (let i = 0; i < list.length; i++) {
		const button = list[i];
		if (!_.isPlainObject(button)) {
			return this.language.translate('buttons[{{i}}] must be an object', { i });
		}
		if (button.action != null && !_.isString(button.action) && !_.isNumber(button.action)) {
			return this.language.translate('buttons[{{i}}].action must be a string', { i });
		}
		if (button.caption != null && !_.isString(button.caption)) {
			return this.language.translate('buttons[{{i}}].caption must be a string', { i });
		}
		if (button.class != null && !_.isString(button.class)) {
			return this.language.translate('buttons[{{i}}].class must be a string', { i });
		}
		if (button.tooltip != null && !_.isString(button.tooltip)) {
			return this.language.translate('buttons[{{i}}].tooltip must be a string', { i });
		}
	}
	return true;
};

Dialog.prototype.onInit = function(dependencies) {
	this.graphileon = dependencies.get(IGraphileon);
	this.language   = dependencies.get(Language);

	this._modalController = null;

	this.setParameters({
		'$dialogType':  'alert',
		'$message':     '',
		'$headerText':  this.language.translate('Attention'),
		'$alertType':   'default',
		'$modalSize':   'modal-sm',
		'$fullscreen':  false,
		'$stayAlive':  'dashboard',
		'$buttons':     [],
		'$promptInput': {}
	});

	const optional = (defaultValue) => ({ default: defaultValue, warn: _.def });

	this.setModelValidation({
		dialogType: [
			value => _.includes(DIALOG_TYPES, value),
			this.language.translate('dialogType must be one of {{values}}', { values: DIALOG_TYPES.join('/') }),
			optional('alert')
		],
		message:    ['isString', optional('')],
		headerText: ['isString', optional(this.language.translate('Attention'))],
		alertType:  [
			value => _.includes(ALERT_TYPES, value),
			this.language.translate('alertType must be one of {{values}}', { values: ALERT_TYPES.join('/') }),
			optional('default')
		],
		modalSize:  [
			value => _.includes(MODAL_SIZES, value),
			this.language.translate('modalSize must be one of {{values}}', { values: MODAL_SIZES.join('/') }),
			optional('modal-sm')
		],
		fullscreen: ['isBooleanParsable', optional(false)],
		buttons:    [this._validateButtons.bind(this), this.language.translate('Invalid buttons')],
		promptInput: [{
			type: [
				value => _.includes(INPUT_TYPES, value),
				this.language.translate('promptInput.type must be one of {{values}}', { values: INPUT_TYPES.join('/') }),
				optional('text')
			],
			value:             ['isStringOrNumber', optional()],
			placeholder:       ['isString', optional()],
			required:          ['isBooleanParsable', optional(false)],
			minLength:         ['isNumber', optional()],
			maxLength:         ['isNumber', optional()],
			min:               ['isStringOrNumber', optional()],
			max:               ['isStringOrNumber', optional()],
			step:              ['isStringOrNumber', optional()],
			pattern:           ['isString', optional()],
			validationMessage: ['isString', optional()],
			autoComplete:      ['isString', optional()],
			autoFocus:         ['isBooleanParsable', optional(false)],
			readOnly:          ['isBooleanParsable', optional(false)],
			disabled:          ['isBooleanParsable', optional(false)]
		}, optional({})]
	});
};

Dialog.prototype.onExecute = async function(input) {
	if (!this.graphileon.viewmanager) {
		log.warn('Dialog: viewmanager not available; nothing to render.');
		this.closeFunctionInstance();
		return false;
	}

	const dialogType = input.dialogType || 'alert';

	const options = {
		type:        dialogType,
		messageText: input.message || '',
		headerText:  input.headerText,
		alertType:   input.alertType,
		modalSize:   input.modalSize,
		fullscreen:  !!input.fullscreen,
		buttons:     input.buttons,
		promptInput: input.promptInput || {},
		onShow: (container, controller) => {
			this._modalController = controller;
		},
		onButtonClick: ({ action, value }) => {
			this.executeTriggers({
				type:   'buttonClicked',
				action: action,
				data:   value != null ? value : null
			});
		},
		onClose: () => {
			this.closeFunctionInstance();
		}
	};

	this.graphileon.viewmanager.modalAlert(options);
	return true;
};

Dialog.prototype.onClose = function() {
	if (this._modalController) {
		try {
			this._modalController.close();
		} catch (e) {
			log.warn('Dialog: failed to close modal on kill:', e);
		}
		this._modalController = null;
	}
	return true;
};

module.exports = Dialog;
